import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap } from "react-leaflet";
import { Switch, TextField, CircularProgress, Slider } from "@mui/material";
import { DateTime } from "luxon";
import L from "leaflet";

// Custom SVG Icon
const svgIcon = `
<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 120 120">
  <circle cx="60" cy="60" r="40" fill="orange" stroke="white" stroke-width="10" />
</svg>
`;

const customIcon = L.divIcon({
  className: '',
  html: svgIcon,
  iconSize: [30, 30],
  iconAnchor: [15, 15], // Center the icon
});

const CenterMapOnUpdate = ({ position, shouldCenter }) => {
  const map = useMap();

  if (map) {
    map.preferCanvas = true
  }

  useEffect(() => {
    if (shouldCenter && position) {
      map.setView(position, 13);
    }
  }, [map, position, shouldCenter]);

  return null;
};

const MachineGPSPanel = ({ metaData, liveData }) => {
  const [gpsData, setGpsData] = useState([]);
  const [isRangeMode, setIsRangeMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    DateTime.now().minus({ hours: 24 }).toJSDate(),
    DateTime.now().toJSDate(),
  ]);
  const [sliderValue, setSliderValue] = useState(0);
  const [shouldCenter, setShouldCenter] = useState(true);

  const intervalRef = useRef(null);

  const fetchLiveData = () => {
    if (!liveData?.LATITUDE || !liveData?.LONGITUDE) {
      console.warn("No GPS data found in liveData.");
      setGpsData([]);
      return;
    }

    const latestPosition = [
      {
        lat: liveData.LATITUDE,
        lon: liveData.LONGITUDE,
        time: liveData.GMT_UNIX
          ? DateTime.fromMillis(liveData.GMT_UNIX * 1000).toISO()
          : DateTime.now().toISO(),
      },
    ];
    setGpsData(latestPosition);
  };

  const fetchGPSData = async () => {
    if (!metaData.serialNumber || !metaData.organization) {
      console.error("Machine metadata is unavailable");
      return;
    }

    if (!isRangeMode) {
      // Use liveData for 'latest' mode
      fetchLiveData();
      return;
    }

    // Fetch GPS data from the API for 'path' mode
    setIsLoading(true);
    try {
      const response = await axios.get(`/api/influx/gps/${metaData.serialNumber}`, {
        params: {
          machineOrg: metaData.organization,
          mode: "path",
          start: DateTime.fromJSDate(dateRange[0]).toISO(),
          end: DateTime.fromJSDate(dateRange[1]).toISO(),
        },
      });

      const data = response.data.filter((point) => point.lat && point.lon);
      setGpsData(data);
      if (data.length === 0) console.warn("No GPS data found from API.");
    } catch (error) {
      console.error("Error fetching GPS data:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGPSData();

    if (!isRangeMode) {
      // Start updating live data every 10 seconds when in latest mode
      intervalRef.current = setInterval(() => {
        fetchLiveData();
      }, 10000);
    } else {
      // Clear interval when switching to route mode
      clearInterval(intervalRef.current);
    }

    return () => {
      clearInterval(intervalRef.current); // Clear interval on unmount or mode change
    };
  }, [isRangeMode, liveData]); // Trigger effect on mode or liveData change

  const mapPosition = useMemo(
    () => (gpsData.length ? [gpsData[0].lat, gpsData[0].lon] : [51.505, -0.09]),
    [gpsData]
  );

  const currentPoint = useMemo(
    () =>
      gpsData[Math.floor((sliderValue / 100) * (gpsData.length - 1))],
    [gpsData, sliderValue]
  );

  const handleDateChange = (index, value) => {
    const newDate = DateTime.fromISO(value).toJSDate();
    setDateRange((prev) => prev.map((d, i) => (i === index ? newDate : d)));
  };

  const renderMap = () => (
    <MapContainer
      center={mapPosition}
      zoom={13}
      style={{
        height: "100%",
        width: "100%",
        borderRadius: "12px",
        zIndex: 0,
      }}
    >
      <CenterMapOnUpdate position={mapPosition} shouldCenter={shouldCenter} />
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      {isRangeMode && gpsData.length > 0 ? (
        <>
          <Polyline positions={gpsData.map(({ lat, lon }) => [lat, lon])} color="blue" />
          {currentPoint && (
            <Marker position={[currentPoint.lat, currentPoint.lon]} icon={customIcon}>
              <Tooltip>
                <div>
                  <p>
                    <strong>Lat:</strong> {currentPoint.lat.toFixed(6)}
                  </p>
                  <p>
                    <strong>Lon:</strong> {currentPoint.lon.toFixed(6)}
                  </p>
                  <p>
                    <strong>Last Updated:</strong>{" "}
                    {DateTime.fromISO(currentPoint.time).toLocaleString({
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hourCycle: "h23", // Ensures 24-hour format
                    })}
                  </p>
                </div>
              </Tooltip>
            </Marker>
          )}
        </>
      ) : (
        gpsData.length > 0 && (
          <Marker position={[gpsData[0].lat, gpsData[0].lon]} icon={customIcon}>
            <Tooltip>
              <div>
                <p>
                  <strong>Lat:</strong> {gpsData[0].lat.toFixed(6)}
                </p>
                <p>
                  <strong>Lon:</strong> {gpsData[0].lon.toFixed(6)}
                </p>
                {!isRangeMode && liveData?.GMT_UNIX && (
                  <p>
                    <strong>Last Updated:</strong>{" "}
                    {DateTime.fromMillis(liveData.GMT_UNIX * 1000).toLocaleString({
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hourCycle: "h23", // Ensures 24-hour format
                    })}
                  </p>
                )}
              </div>
            </Tooltip>
          </Marker>
        )
      )}
    </MapContainer>
  );

  return (
    <div className="flex flex-col h-full shadow rounded-lg">
      <div className="grow overflow-hidden">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <CircularProgress />
          </div>
        ) : (
          renderMap()
        )}
      </div>

      {/* Slider and Info */}
      {isRangeMode && gpsData.length > 0 && (
        <>
          <Slider
            value={sliderValue}
            onChange={(_, value) => setSliderValue(value)}
            className="mt-4"
          />
        </>
      )}
    </div>
  );
};

export default MachineGPSPanel;
